import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import Configuration from '@/agilis/util/Configuration'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'

import FilterTerm from './FilterTerm/FilterTerm.vue'
import i18n from '@/i18n/i18n'

export default {
  name: 'FilterDialog',
  mixins: [toursMixin],

  props: {
    dataTableColumns: {
      type: Object,
      default: () => ({})
    },
    availableFields: {
      type: Array,
      default: () => []
    },
    availableHeaders: Array,
    headers: Array,
    filterRoute: String,
    startingFilter: String
  },

  data () {
    return {
      dialog: false, // flag open/closed
      appliedFilter: null, // filter applied (visible outside the component)
      selectedFilter: null, // filter selected within the component
      selectedItemIndex: null,
      availableFilters: [], // list of available filters, in sync with userDataStore

      isSaving: false,
      isDeleting: false,
      lockDialog: false
    }
  },

  components: {
    FilterTerm
  },

  created () {
    if (this.startingFilter) {
      this.availableFilters = this.getFiltersInProfile()
      const ff = this.availableFilters.find(f => f.id === this.startingFilter)
      const filterHeaders = JSON.parse(JSON.stringify(this.headers))
      if (ff) this.appliedFilter = { filterHeaders, ...ff }
    }
  },

  watch: {
    // Update state when a new item selected in the list
    selectedItemIndex: function (index) {
      if (index !== null) {
        const filterHeaders = JSON.parse(JSON.stringify(this.headers))
        this.selectedFilter = { filterHeaders, ...this.availableFilters[index] }
      } else {
        this.selectedFilter = null
      }
    },

    // Update state when dialog is open/closed:
    dialog: function (newValue) {
      if (newValue) { // opening: reset selection & sync availble from store
        this.availableFilters = this.getFiltersInProfile()
        if (this.appliedFilter) {
          const fIndex = this.availableFilters.findIndex(ff => ff.id === this.appliedFilter.id)
          if (fIndex >= 0) {
            this.selectedItemIndex = fIndex
            this.selectedFilter = this.availableFilters[fIndex]
          }
        }
      } else { // closing: reset selected state
        this.selectedItemIndex = null
        this.selectedFilter = null
      }
    },
    // Keep lockDialog flag consistend with change of isDeleting
    isDeleting: function (newValue) {
      this.lockDialog = this.isSaving || !!newValue
    },
    // Keep lockDialog flag consistend with change of isSaving
    isSaving: function (newValue) {
      this.lockDialog = this.isDeleting || !!newValue
    },
    filtersInProfile: function (newValue) {
      Vue.set(this.selectedFilter, 'filterTerms', newValue.find(f => f.id === this.selectedFilter.id).filterTerms.filter(f => f.field !== null))
      Vue.set(this.selectedFilter, 'sortTerms', newValue.find(f => f.id === this.selectedFilter.id).sortTerms.filter(f => f.sortField !== null))
      Vue.set(this.selectedFilter, 'filterHeaders', newValue.find(f => f.id === this.selectedFilter.id).filterHeaders)
    }
  },

  computed: {
    ...mapGetters({
      filtersInProfile: 'userDataStore/filters',
      'applicationId': 'contextStore/applicationId',
      'workspaceId': 'contextStore/workspaceId',
      'tabId': 'contextStore/tabId',
      'trackerTypeId': 'contextStore/trackerTypeId'
    }),

    _filterRoute () {
      if (this.filterRoute) return this.filterRoute

      let route = `/api/${this.applicationId}`
      if (this.workspaceId) {
        route += `/workspace/${this.workspaceId}`
      }
      if (this.trackerTypeId) {
        route += `/${this.tabId}/${this.trackerTypeId}`
      }
      return route
    },

    selectOptions () {
      const filterInProfile = this.getFiltersInProfile().map(f => ({ value: f.id, text: f.name }))
      if (this.appliedFilter?.temp) {
        filterInProfile.push({ value: this.appliedFilter.id, text: this.appliedFilter.name })
      }
      return filterInProfile
    },

    filterValid () {
      if (!this.selectedFilter) return true

      const valid = this.selectedFilter.filterTerms
        .filter(f => !!f.field)
        .reduce((acc, filterTerm) => {
          const v = Configuration.datatable.validateSearchFilter(filterTerm, this.dataTableColumns)

          if (!v) {
            Vue.set(filterTerm, 'invalid', true)
          } else {
            Vue.delete(filterTerm, 'invalid')
          }

          return acc && v
        }, true)

      return valid
    }
  },

  methods: {
    ...mapActions({
      updateFiltersInProfile: 'userDataStore/setFilters'
    }),

    // Apply a filter and notify parent component
    async applyFilter (temp) {
      if (this.selectedFilter && this.selectedFilter.unsaved && !temp) {
        const saveOk = await this.saveFilter()
        if (!saveOk) return
      }

      this.dialog = false

      if (this.selectedFilter) {
        this.appliedFilter = Object.assign({}, this.selectedFilter, temp
          ? { temp, name: this.selectedFilter.name + ' ' + this.$t('label.temporaryFilter'), id: this.selectedFilter.id + '_temp' }
          : {})
        this.$emit('applyFilter', this.appliedFilter)
      } else {
        this.resetFilter()
      }
    },

    // Add a new filter to the list
    addNewFilter () {
      const newFilter = {
        id: `filter_${Math.random().toString(36).substr(2, 9)}`,
        name: this.$t('label.filterNewLabel'),
        route: this._filterRoute,
        filterTerms: [],
        sortTerms: [],
        unsaved: true,
        locale: i18n.locale
      }
      this.availableFilters.push(newFilter)
      this.selectedItemIndex = this.availableFilters.length - 1
      this.selectedFilter = this.availableFilters[this.selectedItemIndex]
    },

    // Delete a filter from the list and sync with userDataStore (and API)
    async deleteFilter () {
      const forceReset = this.selectedFilter?.id === this.appliedFilter?.id

      const filtersInProfile = this.getFiltersInProfile(false)
      const filtersInProfileUpdated = filtersInProfile.filter(ff => ff.id !== this.selectedFilter.id)

      this.isDeleting = this.selectedFilter.id

      try {
        // Update filters in userDataStore
        await this.updateFiltersInProfile(filtersInProfileUpdated)

        // Reset the applied filter
        if (forceReset) {
          this.$emit('resetFilter')
        }

        // Update component state
        this.availableFilters = this.getFiltersInProfile()
        if (this.availableFilters.length > 0) {
          this.selectedFilter = this.availableFilters[0]
          this.selectedItemIndex = 0
        } else {
          this.selectedFilter = null
          this.selectedItemIndex = null
        }
        this.appliedFilter = this.selectedFilter
      } catch (e) {
        // ... delete process aborted
      }
      this.isDeleting = false
    },

    // Reset applied filter
    resetFilter () {
      this.selectedFilter = null
      this.selectedItemIndex = null
      this.appliedFilter = null
      this.dialog = false

      this.$emit('resetFilter')
    },

    // Save filter: sync with userDataStore (and API)
    async saveFilter () {
      this.markAsUnsaved(false) // mark item as saved

      // Cleanup: remove unset filter/sort terms
      Vue.set(this.selectedFilter, 'filterTerms', this.selectedFilter.filterTerms.filter(f => f.field !== null))
      Vue.set(this.selectedFilter, 'sortTerms', this.selectedFilter.sortTerms.filter(f => f.sortField !== null))
      Vue.set(this.selectedFilter, 'filterHeaders', this.selectedFilter.filterHeaders)

      // Make sure to SAVE ONLY the selected filter, NOT ALL THE FILTERS MODIFIED
      const filtersInProfile = this.getFiltersInProfile(false)
      const filterIndexInProfile = filtersInProfile.findIndex(f => f.id === this.selectedFilter.id)
      if (filterIndexInProfile >= 0) {
        filtersInProfile[filterIndexInProfile] = this.selectedFilter // update the filter
      } else {
        filtersInProfile.push(this.selectedFilter) // append the filter
      }

      this.isSaving = this.selectedFilter.id
      try {
        // Update filters in userDataStore
        await this.updateFiltersInProfile(filtersInProfile)
        this.isSaving = false
        return true
      } catch (e) {
        this.markAsUnsaved() // saved failed restore the flag
        this.isSaving = false
        return false
      }
    },

    // Callback for input name
    changeSelectedFilterName (e) {
      const newValue = e.target.value
      if (!newValue) {
        this.selectedFilter.name = 'Unnnamed Filter'
      }

      this.markAsUnsaved()
    },

    filterChanged (filterDelta) {
      this.markAsUnsaved()
    },

    // Getter for filters in userProfile
    getFiltersInProfile (filterByRoute = true) {
      const ff = JSON.parse(JSON.stringify(this.filtersInProfile || [])) // trick per non modificare le ref a oggetti nello store

      return this.orderByName(filterByRoute ? ff.filter(f => f.route === this._filterRoute) : ff)
    },

    // Falg filter as unsaved
    markAsUnsaved (unsaved = true) {
      if (unsaved) {
        // On change, patch selected filter with the locale when not present
        if (!this.selectFilter.locale) {
          this.$set(this.selectedFilter, 'locale', i18n.locale)
        }

        this.$set(this.selectedFilter, 'unsaved', true) // it might be added: we need to use this tick to make it reactive
      } else {
        this.$delete(this.selectedFilter, 'unsaved') // remove flag => remove the flag so it will not be sent to server
      }
    },

    openDialog () {
      this.dialog = true
    },

    selectFilter (filterId) {
      this.availableFilters = this.getFiltersInProfile()

      const ff = this.availableFilters.find(f => f.id === filterId)

      if (!ff) {
        this.appliedFilter = null
        this.$emit('applyFilter', this.appliedFilter)
      } else if (ff.id !== this.appliedFilter?.id) {
        this.appliedFilter = ff
        this.$emit('applyFilter', this.appliedFilter)
      }
    },

    orderByName (filters) {
      return filters.sort((a, b) =>
        (a.name.toLowerCase() > b.name.toLowerCase())
          ? 1 : ((b.name.toLowerCase() > a.name.toLowerCase())
            ? -1 : 0)
      )
    }
  }
}
